import { useCallback, useState, useEffect } from 'react';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import { LabeledCheckbox } from '../../../generic/Forms';
import { useAddToast } from '../../../../contexts/ToastContext';
import { useAsyncValue } from '../../../../hooks';
import { errorMessagesListFromResponse } from '../../../../helpers/errors';
import { patch } from '../../../../redux/api';
import PropagateModal from '../../shared/SettingsPropagateModal';
import { getEndpoint } from './utils';
import { window } from '../../../../globals';
import type { ContainerIssueSettings } from '../../../../../shared/settings/project/issues';
import type { FailedFetchResponse } from '../../../../helpers/errors';

//  All the propagate checkboxes are disabled by default
const resetCheckboxState: ContainerIssueSettings = {
  projectDefaultExcludeBaseLayerIssuesLicensing: false,
  projectDefaultExcludeBaseLayerIssuesSecurity: false,
  projectDefaultExcludeBaseLayerIssuesQuality: false,
};

//  Small hook for managing the state of the checkboxes + setting up the save function
const useContainerPropagateModal = (isOpen: boolean, onHide: () => void) => {
  const addToast = useAddToast();
  //  Void here because we don't really care about the stored value, we're just using this to track the status of the PATCH request.
  const asyncValue = useAsyncValue<void>();
  const [checkboxes, setCheckboxes] = useState<ContainerIssueSettings>(resetCheckboxState);

  //  Reset the checkboxes when the modal closes.
  //  We always want the user to select which options should propagate,
  //  which is why none should be checked when the user opens the modal
  useEffect(() => {
    setCheckboxes(resetCheckboxState);
  }, [isOpen]);

  const onSavePropagateSettings = useCallback(async () => {
    try {
      asyncValue.setPending();
      await patch<Array<keyof ContainerIssueSettings>>(
        getEndpoint(window.user?.organizationId as number, 'container'),
        _.compact(
          Object.entries(checkboxes).map(([key, value]) => (value ? key : null) as keyof ContainerIssueSettings | null),
        ),
      );
      asyncValue.setResolve();
      //  Show toast and hide the modal after we successfully propagate.
      addToast({ type: 'success', content: 'Settings successfully applied to all projects.' });
      onHide();
    } catch (error) {
      asyncValue.setReject(
        errorMessagesListFromResponse(error as FailedFetchResponse),
      );
    }
  }, [addToast, asyncValue, checkboxes, onHide]);

  return {
    checkboxes,
    setCheckboxes,
    onSavePropagateSettings,
    isLoading: asyncValue.isLoading,
    errors: asyncValue.errors,
  };
};

type Props = {
  //  If the modal is open or not
  isOpen: boolean;
  //  We need the current state of the container settings so that we can
  //  alter the text on the checkboxes so that it makes sense.
  settings: ContainerIssueSettings;
  //  Hide the modal
  onHide: () => void;
};

/**
 * SettingsOrganizationProjectContainerPropagateModal is a like the issues
 * propagate modal, but tailored for just the container settings.
 */
const SettingsOrganizationProjectContainerPropagateModal: VoidFunctionComponent<Props> = ({
  isOpen,
  settings,
  onHide,
}) => {
  const {
    checkboxes,
    setCheckboxes,
    isLoading,
    errors,
    onSavePropagateSettings,
  } = useContainerPropagateModal(isOpen, onHide);

  return (
    <PropagateModal
      settingsCategory="container"
      isOpen={isOpen}
      errors={errors}
      onHide={onHide}
      onSave={onSavePropagateSettings}
      isSaveButtonDisabled={false}
      isSaveButtonLoading={isLoading}
    >
      <LabeledCheckbox
        label={`${settings.projectDefaultExcludeBaseLayerIssuesLicensing ? 'Exclude' : 'Include'} licensing issues on base layers in all projects.`}
        isChecked={checkboxes.projectDefaultExcludeBaseLayerIssuesLicensing}
        onChange={() => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultExcludeBaseLayerIssuesLicensing: !checkboxes.projectDefaultExcludeBaseLayerIssuesLicensing,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={`${settings.projectDefaultExcludeBaseLayerIssuesSecurity ? 'Exclude' : 'Include'} security issues on base layers in all projects.`}
        isChecked={checkboxes.projectDefaultExcludeBaseLayerIssuesSecurity}
        onChange={() => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultExcludeBaseLayerIssuesSecurity: !checkboxes.projectDefaultExcludeBaseLayerIssuesSecurity,
        }))}
        isRippleDisabled
      />
      <LabeledCheckbox
        label={`${settings.projectDefaultExcludeBaseLayerIssuesQuality ? 'Exclude' : 'Include'} quality issues on base layers in all projects.`}
        isChecked={checkboxes.projectDefaultExcludeBaseLayerIssuesQuality}
        onChange={() => setCheckboxes(currentCheckboxes => ({
          ...currentCheckboxes,
          projectDefaultExcludeBaseLayerIssuesQuality: !checkboxes.projectDefaultExcludeBaseLayerIssuesQuality,
        }))}
        isRippleDisabled
      />
    </PropagateModal>
  );
};

export default SettingsOrganizationProjectContainerPropagateModal;
